/**
 * GIWAHS — Public verification ladder page at /factories/verification
 * ---------------------------------------------------------------------
 * Brand statement: GIWAHS is the most trusted sourcing marketplace.
 * No directory has this much rigour behind every badge.
 *
 * Layout: 4-tier comparison · trust-score formula · why-it-matters · FAQ · CTAs.
 * Aesthetic: black-and-white architectural editorial, no AI-slop gradients.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Award, ShieldCheck, FileText, Building2, Camera, Users, Activity, ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';

const TIERS = [
  {
    slug: 'unverified', label: 'Listed', subtitle: 'Public directory entry',
    score: '0 – 39', price: 'Free', tone: 'border-neutral-300 bg-white text-neutral-700',
    pill: 'border-neutral-400 text-neutral-700',
    summary: 'A factory appears on GIWAHS so buyers can search them — but no documentary proof has been submitted yet.',
    proofs: ['Public profile listing', 'Searchable by category, country, certifications listed by the factory'],
    cant: ['No verification badge', 'No tier display on quotes', 'No reference checks'],
    cta: null,
  },
  {
    slug: 'verified', label: 'Verified', subtitle: 'Legal entity confirmed',
    score: '40 – 69', price: 'Free', tone: 'border-neutral-700 bg-neutral-50 text-neutral-900',
    pill: 'border-neutral-900 text-neutral-900 bg-white',
    summary: 'GIWAHS has confirmed this is a real, legally registered factory at the address they claim, with active production.',
    proofs: [
      'Business registration certificate (or local equivalent)',
      'Tax / VAT ID document',
      'Factory address proof (utility bill or lease)',
      '3+ production-floor photos showing real machines and people',
    ],
    cant: ['No third-party audit', 'No buyer references yet'],
    cta: { label: 'Apply for Verified', href: '/supplier/verification' },
  },
  {
    slug: 'gold', label: 'Premium Gold', subtitle: 'Capacity attested + buyer reference',
    score: '70 – 84', price: 'NZ$490 / year', tone: 'border-amber-500 bg-amber-50 text-amber-950',
    pill: 'border-amber-700 text-amber-900 bg-amber-50',
    summary: 'On top of Verified, this factory has demonstrated production capacity with attestations and at least one verifiable buyer reference.',
    proofs: [
      'Everything in Verified',
      '2+ compliance certifications (BSCI / WRAP / GOTS / OEKO-TEX / ISO / etc.)',
      'Capacity attestation form (monthly output, machines, headcount)',
      '1+ signed buyer reference letter or past PO',
    ],
    cant: ['No on-site visit yet'],
    cta: { label: 'Apply for Premium Gold', href: '/supplier/verification' },
  },
  {
    slug: 'elite', label: 'Enterprise Elite', subtitle: 'On-site verified + global compliance',
    score: '85 – 100', price: 'NZ$1,490 / year', tone: 'border-black bg-black text-white',
    pill: 'border-white text-white bg-black',
    summary: 'The top of the ladder. GIWAHS (or a partner auditor) has visited the factory in person within the last 12 months and signed off on labour, quality, and capacity claims.',
    proofs: [
      'Everything in Premium Gold',
      '3+ compliance certifications',
      '2+ signed buyer references',
      'Recent third-party audit report OR GIWAHS on-site visit report',
    ],
    cant: [],
    cta: { label: 'Apply for Enterprise Elite', href: '/supplier/verification' },
  },
];

const SIGNALS = [
  { icon: FileText, max: 20, label: 'Legal entity proof', detail: 'Business registration · tax/VAT · address proof' },
  { icon: Camera,   max: 10, label: 'Production capability', detail: 'Verified production-floor photography' },
  { icon: ShieldCheck, max: 25, label: 'Compliance certifications', detail: 'BSCI, WRAP, GOTS, OEKO-TEX, ISO… (+5 each, capped 25)' },
  { icon: Building2,max: 5,  label: 'Capacity attestation', detail: 'Monthly output, machines, workforce' },
  { icon: Users,    max: 10, label: 'Buyer references', detail: 'Signed letters or verified past POs (+5 each, capped 10)' },
  { icon: Award,    max: 15, label: 'On-site or third-party audit', detail: 'GIWAHS visit OR recognised auditor (≤ 12 months)' },
  { icon: Activity, max: 15, label: 'Response performance', detail: 'RFQ response rate + quote velocity + completed-transaction rate + buyer feedback' },
];

const VerificationLadder = () => {
  return (
    <div data-testid="verification-ladder-page" className="min-h-screen bg-white text-neutral-900">
      <SEO
        title="GIWAHS Verification — The 4-tier trust ladder for global sourcing"
        description="GIWAHS is the most trusted sourcing marketplace. Every factory tier is earned via documents, capacity attestation, buyer references, and on-site audits."
      />

      {/* Hero */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="label-overline text-neutral-500">Verification framework · v1</p>
              <h1 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl lg:text-6xl">
                The 4-tier trust ladder<br />for global sourcing.
              </h1>
              <p className="mt-6 max-w-2xl text-base text-neutral-700 md:text-lg">
                Most B2B directories list anyone who claims a factory. GIWAHS doesn't. Every badge on this platform is <em>earned</em> — through documents, capacity attestation, signed buyer references, and (for Enterprise) an on-site audit within the last 12 months.
              </p>
              <p className="mt-4 max-w-2xl text-base text-neutral-700">
                Buyers see the badge, not the paperwork. Suppliers earn the badge by completing the ladder.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/supplier/verification" data-testid="ladder-cta-verify" className="inline-flex items-center gap-2 border border-black bg-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-neutral-800">
                  Get my factory verified <ArrowRight size={14} strokeWidth={1.75} />
                </Link>
                <Link to="/factories" data-testid="ladder-cta-browse" className="inline-flex items-center gap-2 border border-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider hover:bg-black hover:text-white">
                  Browse verified factories
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="border border-black bg-white p-6">
                <p className="label-overline text-neutral-500">In plain language</p>
                <ul className="mt-3 space-y-3 text-sm text-neutral-800">
                  <li className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-emerald-700" /> Every tier requires submitted documents reviewed by a GIWAHS admin.</li>
                  <li className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-emerald-700" /> Buyers never see uploaded paperwork — only the verified badge.</li>
                  <li className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-emerald-700" /> Tier expires automatically (24 months Verified · 12 months Premium · 12 months Elite) — no permanent claims.</li>
                  <li className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-emerald-700" /> Trust score is computed live from 7 signals (see below) — not a marketing number.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="label-overline text-neutral-500">The ladder</p>
          <h2 className="mt-2 font-display text-3xl tracking-tight md:text-4xl">Four tiers · earned, not bought</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {TIERS.map((t) => (
              <article key={t.slug} data-testid={`ladder-tier-${t.slug}`} className={`border-2 p-6 ${t.tone}`}>
                <header className="flex items-start justify-between gap-3 border-b border-current/30 pb-4">
                  <div>
                    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${t.pill}`}>
                      <Award size={10} strokeWidth={1.5} /> {t.label}
                    </span>
                    <h3 className="mt-3 font-display text-2xl tracking-tight">{t.label}</h3>
                    <p className="mt-1 font-mono-data text-[11px] uppercase tracking-wider opacity-80">{t.subtitle}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-data text-[10px] uppercase tracking-wider opacity-70">Score</div>
                    <div className="font-display text-xl tracking-tight">{t.score}</div>
                    <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider opacity-70">{t.price}</div>
                  </div>
                </header>
                <p className="mt-4 text-sm leading-relaxed opacity-90">{t.summary}</p>
                <div className="mt-5">
                  <div className="label-overline opacity-70">Proof required</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {t.proofs.map((p, i) => (
                      <li key={i} className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0" /> {p}</li>
                    ))}
                  </ul>
                </div>
                {t.cant.length > 0 && (
                  <div className="mt-4">
                    <div className="label-overline opacity-70">What this tier does NOT claim</div>
                    <ul className="mt-2 space-y-1.5 text-sm opacity-80">
                      {t.cant.map((c, i) => <li key={i}>— {c}</li>)}
                    </ul>
                  </div>
                )}
                {t.cta && (
                  <Link to={t.cta.href} data-testid={`ladder-cta-${t.slug}`} className={`mt-5 inline-flex items-center gap-2 border px-3 py-2 font-mono-data text-[10px] uppercase tracking-wider ${t.slug === 'elite' ? 'border-white hover:bg-white hover:text-black' : 'border-current hover:bg-current hover:text-white'}`}>
                    {t.cta.label} <ArrowRight size={12} strokeWidth={1.75} />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Trust-score formula */}
      <section className="border-b border-black bg-neutral-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="label-overline text-neutral-500">The formula</p>
              <h2 className="mt-2 font-display text-3xl tracking-tight md:text-4xl">7 signals · 100 points · live</h2>
              <p className="mt-4 text-base text-neutral-700">
                The trust score on every GIWAHS factory profile is calculated from these seven signals. There is no marketing-team adjustment, no manual inflation, no boosted listings. The number recomputes the moment a new document or a new quote outcome lands in the database.
              </p>
              <p className="mt-4 text-base text-neutral-700">
                The score determines the tier automatically — except where a GIWAHS admin has explicitly overridden the tier with a documented note (always visible to the supplier).
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="border border-black bg-white">
                {SIGNALS.map((s, i) => (
                  <div key={s.label} data-testid={`signal-${i}`} className={`flex items-start gap-4 p-5 ${i < SIGNALS.length - 1 ? 'border-b border-neutral-200' : ''}`}>
                    <s.icon size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-neutral-700" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-display text-base tracking-tight">{s.label}</div>
                        <div className="font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">{s.max} pts</div>
                      </div>
                      <div className="mt-1 text-sm text-neutral-600">{s.detail}</div>
                    </div>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-2 border-t-2 border-black p-5">
                  <div className="font-display text-lg tracking-tight">Total</div>
                  <div className="font-display text-lg tracking-tight">100 pts</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="label-overline text-neutral-500">Why this matters</p>
          <h2 className="mt-2 font-display text-3xl tracking-tight md:text-4xl">For buyers · for suppliers</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="border border-black p-6">
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">For buyers</p>
              <h3 className="mt-2 font-display text-xl tracking-tight">Source from factories with real documentation, not stock-photo websites.</h3>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                <li>· Every Verified factory has uploaded business registration + tax ID + address proof + production-floor photos.</li>
                <li>· Every Premium Gold has buyer references on file — we'll connect you if you ask.</li>
                <li>· Every Elite has been visited in the last 12 months — by GIWAHS or a recognised auditor.</li>
                <li>· If a tier expires, the badge drops automatically — no expired claims linger.</li>
              </ul>
            </div>
            <div className="border border-black p-6">
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">For factories</p>
              <h3 className="mt-2 font-display text-xl tracking-tight">A higher tier = more inbound RFQs from serious buyers.</h3>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                <li>· Verified is free. There is no excuse not to be Verified.</li>
                <li>· Premium Gold and Enterprise Elite are paid and reserved for factories serious about export contracts.</li>
                <li>· Your tier appears on every quote you send — buyers see it before reading your numbers.</li>
                <li>· Response performance feeds the score live, so being fast and reliable is directly rewarded.</li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/supplier/verification" data-testid="why-cta-verify" className="inline-flex items-center gap-2 border border-black bg-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-neutral-800">
              Start verification <ArrowRight size={14} strokeWidth={1.75} />
            </Link>
            <Link to="/rfq" data-testid="why-cta-rfq" className="inline-flex items-center gap-2 border border-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider hover:bg-black hover:text-white">
              Submit an RFQ
            </Link>
            <Link to="/contact" data-testid="why-cta-contact" className="inline-flex items-center gap-2 border border-black px-5 py-3 font-mono-data text-[11px] uppercase tracking-wider hover:bg-black hover:text-white">
              Talk to the team
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-black bg-neutral-50">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <p className="label-overline text-neutral-500">FAQ</p>
          <h2 className="mt-2 font-display text-3xl tracking-tight">Things buyers and suppliers ask</h2>
          <dl className="mt-10 space-y-8">
            <div>
              <dt className="font-display text-lg tracking-tight">Can I buy a higher tier without earning it?</dt>
              <dd className="mt-2 text-sm text-neutral-700">No. Tiers above Verified are paid, but payment unlocks <em>the review</em>, not the badge. If your documentation doesn't support Gold or Elite, you'll be approved at the highest tier your evidence supports.</dd>
            </div>
            <div>
              <dt className="font-display text-lg tracking-tight">Why do tiers expire?</dt>
              <dd className="mt-2 text-sm text-neutral-700">A 3-year-old audit isn't a meaningful claim today. Verified is valid for 24 months; Premium Gold and Enterprise Elite are valid for 12 months. Renewals are lightweight — re-upload anything that has changed.</dd>
            </div>
            <div>
              <dt className="font-display text-lg tracking-tight">Are uploaded documents visible to buyers?</dt>
              <dd className="mt-2 text-sm text-neutral-700">Never. Only GIWAHS admins see uploaded paperwork, and only via signed URLs that expire in 60 seconds. Buyers see the badge and the trust score — nothing more.</dd>
            </div>
            <div>
              <dt className="font-display text-lg tracking-tight">Can a tier be revoked?</dt>
              <dd className="mt-2 text-sm text-neutral-700">Yes. If buyer complaints surface fraud or material misrepresentation, a tier is revoked immediately and the factory falls back to Unverified pending review.</dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
};

export default VerificationLadder;
